import { useState } from "react";
import { useEvent } from "../../../context/EventContext";

//  * Agenda is a component that displays the sub events of the selected event based on the location selected by the user.
//  * It also displays the day-wise sub events of the selected event.
//  * The user can select a location and view the filtered sub events based on the selected location.
//  * The user can also view the day-wise sub events of the selected event.
//  * The component takes in the selectedEvent state from the EventContext and displays the sub events accordingly.
//  * The component also handles the location dropdown and day-wise sub events button clicks.
//  * It filters the sub events based on the selected location and displays the day-wise sub events of the selected event.
//  * The component also handles the back button click to toggle the view between the location dropdown and day-wise sub events table.
const Agenda = () => {
  const { selectedEvent } = useEvent();

  const subEvents = selectedEvent[7] ? selectedEvent[7] : [];
  const locations = selectedEvent[1] ? selectedEvent[1] : [];
  //   const locationID = subEvents.map((subEvent) => subEvent.location);
  const speakers = selectedEvent[2] ? selectedEvent[2] : [];
  const sponsors = selectedEvent[4] ? selectedEvent[4] : [];

  // Track selected location and toggle view
  const [selectedLocation, setSelectedLocation] = useState("");
  const [showDayWiseTable, setShowDayWiseTable] = useState(false);
  const [visibleButton, setVisibleButton] = useState(true);

  // Filter locations based on matching _id with subEvents' locations
  const locationIDs = subEvents.map((subEvent) => subEvent.location);
  const locationDetail = locations.filter((location) =>
    locationIDs.includes(location._id)
  );

  // Filter subEvents by selected location
  const filteredSubEvents = subEvents.filter(
    (subEvent) =>
      selectedLocation === "" || subEvent.location === selectedLocation
  );

  //   * Handles the change event of the location dropdown.
  //   * Sets the selected location in the state and hides the "Day-Wise SubEvents" button.
  const handleLocationChange = (e) => {
    setSelectedLocation(e.target.value);
    setVisibleButton(false);
  };

  //   * Handles the click event of the "Day-Wise SubEvents" button.
  //   * Sets the showDayWiseTable state to true to show the day-wise sub events table.
  const handleShowDayWise = () => {
    setShowDayWiseTable(true);
  };

  //   * Handles the click event of the "Back" button in the day-wise sub events table.
  //   * Resets the component state to show the location dropdown and the "Day-Wise SubEvents" button,
  //   * and clears the selected location.
  const handleBackButton = () => {
    setShowDayWiseTable(false); // Reset to show the location dropdown
    setVisibleButton(true); // Show the "Day-Wise SubEvents" button again
    setSelectedLocation(""); // Clear the selected location
  };

  return (
    <div className="agenda-section-details">
      {!showDayWiseTable ? (
        <>
          {/* Location Filter Dropdown */}
          <div>
            <label>Select Location: </label>
            <select onChange={handleLocationChange} value={selectedLocation}>
              <option value="">All Locations</option>
              {locations.map((location) => (
                <option key={location._id} value={location._id}>
                  {location.space_name}
                </option>
              ))}
            </select>
          </div>

          {/* Button to show day-wise subEvents */}
          {visibleButton && (
            <button className="add-space" onClick={handleShowDayWise}>
              Day-Wise SubEvents
            </button>
          )}

          {/* Filtered SubEvents Table */}
          <>
            {selectedLocation && (
              <>
                <table>
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSubEvents.length > 0 ? (
                      filteredSubEvents.map((subEvent) => {
                        const matchingLocation = locationDetail.find(
                          (location) => location._id === subEvent.location
                        );
                        const matchingSpeakers = speakers.filter((speaker) =>
                          subEvent.speakers.includes(speaker._id)
                        );
                        const matchingSponsors = sponsors.filter((sponsor) =>
                          subEvent.sponsors.includes(sponsor._id)
                        );

                        return (
                          <tr key={subEvent._id}>
                            <td>
                              {new Date(subEvent.start).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}{" "}
                              -{" "}
                              {new Date(subEvent.end).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </td>
                            <td>
                              <p>Event name: {subEvent.title}</p>

                              {/* Display matching location details */}
                              {matchingLocation && (
                                <>
                                  <p>
                                    Location Name: {matchingLocation.space_name}
                                  </p>
                                  <p>
                                    Location Type: {matchingLocation.space_type}
                                  </p>
                                  <p>
                                    Location Code: {matchingLocation.space_code}
                                  </p>
                                </>
                              )}

                              {/* Display matching speaker names */}
                              {matchingSpeakers.length > 0 && (
                                <p>
                                  Speaker Name:{" "}
                                  {matchingSpeakers
                                    .map((speaker) => speaker.speaker_name)
                                    .join(", ")}
                                </p>
                              )}

                              {/* Display matching sponsor names */}
                              {matchingSponsors.length > 0 && (
                                <p>
                                  Sponsor Name:{" "}
                                  {matchingSponsors
                                    .map((sponsor) => sponsor.company_name)
                                    .join(", ")}
                                </p>
                              )}

                              <p>
                                <span>Is Open For All: </span>
                                {subEvent.isOpen === "yes" ? "YES" : "NO"}
                              </p>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="2">
                          No events available for the selected location.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <button
                  className="add-space"
                  onClick={() => handleBackButton()}
                >
                  Back
                </button>
              </>
            )}
          </>
        </>
      ) : (
        <>
          {/* Day-Wise SubEvents Table */}
          <table>
            <thead>
              <tr>
                <th>Day</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubEvents.map((subEvent, index) => {
                const matchingLocation = locationDetail.find(
                  (location) => location._id === subEvent.location
                );
                const matchingSpeakers = speakers.filter((speaker) =>
                  subEvent.speakers.includes(speaker._id)
                );
                const matchingSponsors = sponsors.filter((sponsor) =>
                  subEvent.sponsors.includes(sponsor._id)
                );

                return (
                  <tr key={subEvent._id}>
                    <td>Day - {index + 1}</td>
                    <td>
                      Start:
                      {new Date(subEvent.start).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}{" "}
                      - End:
                      {new Date(subEvent.end).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                      <p>Event name: {subEvent.title}</p>
                      {/* Display matching location details */}
                      {matchingLocation && (
                        <>
                          <p>Location Name: {matchingLocation.space_name}</p>
                          <p>Location Type: {matchingLocation.space_type}</p>
                          <p>Location Code: {matchingLocation.space_code}</p>
                        </>
                      )}
                      {/* Display matching speaker names */}
                      {matchingSpeakers.length > 0 && (
                        <p>
                          Speaker Name:{" "}
                          {matchingSpeakers
                            .map((speaker) => speaker.speaker_name)
                            .join(", ")}
                        </p>
                      )}
                      {/* Display matching sponsor names */}
                      {matchingSponsors.length > 0 && (
                        <p>
                          Sponsor Name:{" "}
                          {matchingSponsors
                            .map((sponsor) => sponsor.company_name)
                            .join(", ")}
                        </p>
                      )}
                      <p>
                        <span>Is Open For All: </span>
                        {subEvent.isOpen === true ? "YES" : "NO"}
                      </p>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <button className="add-space" onClick={() => handleBackButton()}>
            Back
          </button>
        </>
      )}
    </div>
  );
};

export default Agenda;
